import { App, Button, Form, Input } from 'antd';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import type { ResetFolderPasswordPayload } from '../../../interfaces/FoldersApiService';
import { FoldersService } from '../../../services/foldersApiService';
import { getApiErrorCode } from '../../../utils/apiErrorCode';
import { resolveFolderPasswordApiErrorMessage } from '../../../utils/folderPasswordApiErrors';

// Instantiate the foldersService to manage OTP/email password related API calls.
const foldersService = new FoldersService();

// Cooldown period between OTP requests in milliseconds.
const OTP_COOLDOWN_MS = 30_000;

/**
 * Formats a duration in milliseconds as MM:SS.
 * @param ms Time in milliseconds.
 * @returns Time in MM:SS format.
 */
function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

// Props accepted by the ResetFilePasswordForm component.
export interface ResetFilePasswordFormProps {
  folderId: number;
  onSuccess?: () => void;
  onClose?: () => void;
}

export default function ResetFilePasswordForm({
  folderId,
  onSuccess,
  onClose,
}: ResetFilePasswordFormProps) {
  const { t } = useTranslation();
  const { message, notification } = App.useApp();
  const [form] = Form.useForm<{ otpCode: string; newPassword: string }>();

  // Tracks whether an OTP request is currently loading.
  const [isRequestingOtp, setIsRequestingOtp] = useState(true);
  // True after an OTP has been successfully sent during this session.
  const [otpSent, setOtpSent] = useState(false);
  // Cooldown timer left (ms) until a new OTP can be requested.
  const [timeLeft, setTimeLeft] = useState(0);
  // Expiration timestamp for current cooldown, or null if none.
  const [expirationTime, setExpirationTime] = useState<number | null>(null);
  // Indicates password reset form is submitting.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Captures any error to display on submission.
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * Handles requesting an OTP for password reset.
   * - Triggers loading state, attempts request, and starts cooldown.
   * - Shows appropriate feedback depending on server response.
   */
  const requestOtp = useCallback(async () => {
    setIsRequestingOtp(true);
    setSubmitError(null);
    try {
      await foldersService.requestResetPasswordOtp(folderId);
      setOtpSent(true);
      const expire = Date.now() + OTP_COOLDOWN_MS;
      setExpirationTime(expire);
      setTimeLeft(OTP_COOLDOWN_MS);
      message.success(t('modal.resetFilePasswordForm.otpSentToEmail'));
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      const code = getApiErrorCode((err as { response?: unknown })?.response);
      if (status === 404 || code === ApiErrorEnum.ObjectNotFound) {
        notification.error({ message: t('errors.genericError') });
      } else {
        notification.error({ message: resolveFolderPasswordApiErrorMessage(err, t) });
      }
    } finally {
      setIsRequestingOtp(false);
    }
  }, [folderId, notification, t]);

  /**
   * On component mount or whenever folderId changes, auto-request OTP.
   * Runs on first render, ensures correct folder context.
   * Handles cleanup if the component unmounts or is rerendered early.
   */
  useEffect(() => {
    let cancelled = false;
    setIsRequestingOtp(true);
    setSubmitError(null);
    foldersService
      .requestResetPasswordOtp(folderId)
      .then(() => {
        if (cancelled) return;
        setOtpSent(true);
        const expire = Date.now() + OTP_COOLDOWN_MS;
        setExpirationTime(expire);
        setTimeLeft(OTP_COOLDOWN_MS);
        message.success(t('modal.resetFilePasswordForm.otpSentToEmail'));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { status?: number })?.status;
        const code = getApiErrorCode((err as { response?: unknown })?.response);
        if (status === 404 || code === ApiErrorEnum.ObjectNotFound) {
          notification.error({ message: t('errors.genericError') });
        } else {
          notification.error({ message: resolveFolderPasswordApiErrorMessage(err, t) });
        }
      })
      .finally(() => {
        if (!cancelled) setIsRequestingOtp(false);
      });
    // Cleanup prevents state changes after component unmount.
    return () => {
      cancelled = true;
    };
  }, [folderId, message, notification, t]);

  /**
   * Manages the timer for OTP request cooldown.
   * Updates countdown each second, and resets when finished.
   */
  useEffect(() => {
    if (expirationTime === null) return;
    const interval = setInterval(() => {
      const now = Date.now();
      const diff = expirationTime - now;
      if (diff <= 0) {
        setExpirationTime(null);
        setTimeLeft(0);
        clearInterval(interval);
      } else {
        setTimeLeft(diff);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [expirationTime]);

  /**
   * Handles submitting the reset password form.
   * - Validates the OTP code format.
   * - On success, submits payload to API.
   * - Updates UI and invokes callback on success or error.
   */
  const handleSubmit = useCallback(
    async (values: { otpCode: string; newPassword: string }) => {
      setSubmitError(null);
      const otpCodeNum = parseInt(values.otpCode, 10);
      // Validates OTP is 6-digit number.
      if (Number.isNaN(otpCodeNum) || values.otpCode.length !== 6) {
        const errorMsg = t('modal.resetFilePasswordForm.wrongOtpCode');
        setSubmitError(errorMsg);
        notification.error({ message: errorMsg });
        return;
      }
      setIsSubmitting(true);
      try {
        const payload: ResetFolderPasswordPayload = {
          otpCode: otpCodeNum,
          newPassword: values.newPassword,
        };
        await foldersService.resetFolderPassword(folderId, payload);
        message.success(t('modal.resetFilePasswordForm.passwordResetSuccess'));
        onSuccess?.();
      } catch (err: unknown) {
        const status = (err as { status?: number })?.status;
        let errorMsg: string;
        if (status === 401) {
          errorMsg = t('modal.resetFilePasswordForm.wrongOtpCode');
        } else {
          errorMsg = resolveFolderPasswordApiErrorMessage(err, t);
        }
        setSubmitError(errorMsg);
        notification.error({ message: errorMsg });
      } finally {
        setIsSubmitting(false);
      }
    },
    [folderId, message, notification, onSuccess, t],
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Success hint after OTP has been sent */}
      {otpSent && (
        <p className="text-green-dark text-center text-sm">
          {t('modal.resetFilePasswordForm.otpSentToEmail')}
        </p>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="[&_.ant-form-item]:mb-3!"
      >
        {/* OTP input field */}
        <Form.Item
          label={t('modal.resetFilePasswordForm.otpCodeLabel')}
          name="otpCode"
          rules={[
            { required: true, message: t('modal.resetFilePasswordForm.otpRequired') },
            { len: 6, message: t('modal.resetFilePasswordForm.otpMustBe6') },
          ]}
        >
          <Input.OTP
            length={6}
            size="large"
            className="w-full"
            inputMode="numeric"
            disabled={isSubmitting}
            separator={() => <span>-</span>}
          />
        </Form.Item>

        {/* Timer or OTP request button depending on cooldown state */}
        <div className="pb-2">
          {timeLeft > 0 ? (
            <p className={clsx('min-h-6 text-center text-xs', isRequestingOtp && 'blur-md')}>
              {t('common.youCanRequestANewOtpIn')} {formatTime(timeLeft)}
            </p>
          ) : (
            <div
              className={clsx(
                'flex w-full items-center justify-center gap-1 text-xs',
                isRequestingOtp && 'blur-md',
              )}
            >
              <Button
                type="link"
                size="small"
                className="text-blue-dark! hover:text-azure! p-0! text-center! underline!"
                loading={isRequestingOtp}
                disabled={timeLeft > 0}
                onClick={requestOtp}
              >
                {t('common.clickHereToGenerateNewOtp')}
              </Button>
            </div>
          )}
        </div>

        {/* New password input field */}
        <Form.Item
          label={t('modal.resetFilePasswordForm.newPasswordLabel')}
          name="newPassword"
          rules={[
            { required: true, message: t('modal.resetFilePasswordForm.newPasswordRequired') },
            { min: 4, message: t('modal.resetFilePasswordForm.newPasswordMin') },
          ]}
        >
          <Input.Password size="large" disabled={isSubmitting} />
        </Form.Item>

        {/* Displays error messages related to form submission */}
        {submitError && (
          <p className="text-red-dark text-center text-sm" role="alert">
            {submitError}
          </p>
        )}

        {/* Bottom buttons: Cancel and Submit/Reset password */}
        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
            {t('modal.resetFilePasswordForm.resetPassword')}
          </Button>
        </div>
      </Form>
    </div>
  );
}
